/**
 * École Nid Douillet - CMS API Service
 * 
 * Content Management System API for public website
 */

import { BaseApiService } from './api';
import { ApiResponse } from '@/types';

// CMS types
export interface CMSContent {
  id: number;
  title: string;
  slug: string;
  content_type: 'page' | 'post' | 'news' | 'announcement';
  language: 'fr' | 'ar';
  excerpt?: string;
  content: string;
  meta_title?: string;
  meta_description?: string;
  featured_image?: string;
  tags: string[];
  is_published: boolean;
  display_order: number;
  view_count: number;
  published_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CMSContentList {
  content: CMSContent[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface CMSStats {
  statistics: {
    total_content: number;
    published_content: number;
    draft_content: number;
    pages: number;
    posts: number;
    news: number;
    french_content: number;
    arabic_content: number;
    total_views: number;
  };
  recent_content: Array<{
    title: string;
    content_type: string;
    language: string;
    published_at: string;
    view_count: number;
  }>;
  popular_content: Array<{
    title: string;
    content_type: string;
    language: string;
    view_count: number;
    published_at: string;
  }>;
}

export interface CreateContentRequest {
  title: string;
  slug: string;
  content_type: 'page' | 'post' | 'news' | 'announcement';
  language?: 'fr' | 'ar';
  excerpt?: string;
  content: string;
  meta_title?: string;
  meta_description?: string;
  featured_image?: string;
  tags?: string[];
  is_published?: boolean;
  display_order?: number;
}

export interface UpdateContentRequest extends Partial<CreateContentRequest> {}

export interface CMSQueryParams {
  type?: 'page' | 'post' | 'news' | 'announcement';
  language?: 'fr' | 'ar';
  status?: 'published' | 'draft';
  page?: number;
  limit?: number;
}

/**
 * CMS API Service for Public Website
 */
export class CMSApiService extends BaseApiService {
  /**
   * Get all published content for public website
   */
  async getPublishedContent(params?: Pick<CMSQueryParams, 'type' | 'language'>): Promise<ApiResponse<CMSContent[]>> {
    return this.get('/cms/content/published', { params });
  }

  /**
   * Get content by slug for public display
   */
  async getContentBySlug(slug: string, language: 'fr' | 'ar' = 'fr'): Promise<ApiResponse<CMSContent>> {
    return this.get(`/cms/content/slug/${slug}`, { params: { language } });
  }

  /**
   * Get published pages
   */
  async getPublishedPages(language: 'fr' | 'ar' = 'fr'): Promise<ApiResponse<CMSContent[]>> {
    return this.getPublishedContent({ type: 'page', language });
  }

  /**
   * Get published news
   */
  async getPublishedNews(language: 'fr' | 'ar' = 'fr'): Promise<ApiResponse<CMSContent[]>> {
    return this.getPublishedContent({ type: 'news', language });
  }

  /**
   * Get published announcements
   */
  async getPublishedAnnouncements(language: 'fr' | 'ar' = 'fr'): Promise<ApiResponse<CMSContent[]>> {
    return this.getPublishedContent({ type: 'announcement', language });
  }

  /**
   * Get published posts
   */
  async getPublishedPosts(language: 'fr' | 'ar' = 'fr'): Promise<ApiResponse<CMSContent[]>> {
    return this.getPublishedContent({ type: 'post', language });
  }

  /**
   * Get home page content
   */
  async getHomePage(language: 'fr' | 'ar' = 'fr'): Promise<ApiResponse<CMSContent>> {
    return this.getContentBySlug('accueil', language);
  }

  /**
   * Get about page content
   */
  async getAboutPage(language: 'fr' | 'ar' = 'fr'): Promise<ApiResponse<CMSContent>> {
    return this.getContentBySlug('a-propos', language);
  }

  /**
   * Get contact page content
   */
  async getContactPage(language: 'fr' | 'ar' = 'fr'): Promise<ApiResponse<CMSContent>> {
    return this.getContentBySlug('contact', language);
  }
}

/**
 * CMS Admin API Service (Director only)
 */
export class CMSAdminApiService extends BaseApiService {
  /**
   * Get all content with admin access
   */
  async getAllContent(params?: CMSQueryParams): Promise<ApiResponse<CMSContentList>> {
    return this.get('/cms/content', { params });
  }

  /**
   * Create new content
   */
  async createContent(data: CreateContentRequest): Promise<ApiResponse<CMSContent>> {
    return this.post('/cms/content', data);
  }

  /**
   * Update existing content
   */
  async updateContent(id: number, data: UpdateContentRequest): Promise<ApiResponse<CMSContent>> {
    return this.put(`/cms/content/${id}`, data);
  }

  /**
   * Delete content
   */
  async deleteContent(id: number): Promise<ApiResponse<void>> {
    return this.delete(`/cms/content/${id}`);
  }

  /**
   * Get content statistics
   */
  async getContentStats(): Promise<ApiResponse<CMSStats>> {
    return this.get('/cms/stats');
  }

  /**
   * Publish content
   */
  async publishContent(id: number): Promise<ApiResponse<CMSContent>> {
    return this.updateContent(id, { is_published: true });
  }

  /**
   * Unpublish content
   */
  async unpublishContent(id: number): Promise<ApiResponse<CMSContent>> {
    return this.updateContent(id, { is_published: false });
  }

  /**
   * Generate slug from title
   */
  generateSlug(title: string): string {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .trim()
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
  }

  /**
   * Validate content data
   */
  validateContent(data: CreateContentRequest | UpdateContentRequest): string[] {
    const errors: string[] = [];

    if ('title' in data && (!data.title || data.title.trim().length === 0)) {
      errors.push('Le titre est requis');
    }

    if ('title' in data && data.title && data.title.length > 200) {
      errors.push('Le titre ne peut pas dépasser 200 caractères');
    }

    if ('slug' in data && data.slug && !/^[a-z0-9-]+$/.test(data.slug)) {
      errors.push('Le slug ne peut contenir que des lettres minuscules, chiffres et tirets');
    }

    if ('content' in data && (!data.content || data.content.trim().length === 0)) {
      errors.push('Le contenu est requis');
    }

    if ('excerpt' in data && data.excerpt && data.excerpt.length > 500) {
      errors.push('L\'extrait ne peut pas dépasser 500 caractères');
    }

    if ('meta_title' in data && data.meta_title && data.meta_title.length > 60) {
      errors.push('Le titre meta ne peut pas dépasser 60 caractères');
    }

    if ('meta_description' in data && data.meta_description && data.meta_description.length > 160) {
      errors.push('La description meta ne peut pas dépasser 160 caractères');
    }

    return errors;
  }
}

// Export singleton instances
export const cmsApi = new CMSApiService();
export const cmsAdminApi = new CMSAdminApiService();
